import React, { useEffect } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../../components/custom/BackButton';
import { createdUpdatedInfo, getTsDate } from '../../../utils';
import ActionIcon from '../../../components/custom/ActionIcon';
import { Device } from '../../../globals/interfaces';
import useTypedForm from '../../../components/custom/hookForm/useTypedForm';
import { useAppContext } from '../../../context';
import { yupResolver } from '@hookform/resolvers/yup';
import { createSchema, updateSchema } from './yupSchema';
import CustomSwitch from '../../../components/custom/CustomSwitch';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    selectedDevice: Device | undefined;
    unit: string;
    createDevice: (device: Device) => Promise<void>;
    updateDevice: (device: Device) => Promise<void>;
    deleteDevice: (device: Device) => Promise<void>;
}

export default function ModalManage({
    modal,
    setModal,
    selectedDevice,
    unit,
    createDevice,
    updateDevice,
    deleteDevice
}: Props) {

    const { user } = useAppContext();
    const update = Boolean(selectedDevice);
    const {
        FormInput,
        trigger,
        reset,
        handleSubmit,
        setValue,
        watch,
        formState: {
            isValid, 
            isDirty
        }
    } = useTypedForm<Device>({
        mode: 'all',
        resolver: update ? yupResolver(updateSchema) : yupResolver(createSchema)
    });

    const hidden = watch('hidden');

    useEffect(() => {
        if (selectedDevice) {
            reset(selectedDevice);
        } else {
            const now = getTsDate();
            reset({
                deviceId: '',
                deviceName: '',
                hidden: null,
                createdAt: now,
                createdBy: user.s_email,
                modifiedAt: now,
                modifiedBy: user.s_email,
                unit
            });
        }
    }, [modal, selectedDevice, unit]);

    const handleCreateUpdate = async (device: Device) => {
        if (update) {
            updateDevice(device);
        } else {
            createDevice(device);
        }
    }

    const toggle = () => setModal(!modal);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>{update ? 'Update' : 'Add'} Device</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FormInput name='deviceId' label={'Device ID'} />
                <FormInput name='deviceName' label={'Device Name'} />
                <CustomSwitch 
                    label={'Hidden'}
                    value={hidden}
                    handleSwitch={() => setValue('hidden', !hidden, { shouldDirty: true, shouldTouch: true, shouldValidate: true })}
                />
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    {
                        update && (
                            <Label>
                                {createdUpdatedInfo({selectedDevice})}
                            </Label>
                        )
                    }
                    <FooterButtonsContainer update={update}>
                        {
                            (update && selectedDevice) && (
                                <ActionIcon 
                                    type={'delete'} 
                                    onClick={() => deleteDevice(selectedDevice)} 
                                />
                            )
                        }
                        <ActionIcon 
                            type={'save'} 
                            onClick={handleSubmit(handleCreateUpdate)} 
                            disabled={!isValid || !isDirty}
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;